import React from "react";
import { Outlet, Navigate } from "react-router-dom";
import { useRole, UserRole } from "../hooks/useRole";

interface RequireRolesProps {
  /** The set of roles that are permitted to access the guarded routes. */
  roles: UserRole[];
}

/**
 * RequireRoles — route guard that restricts access to a specific set of roles.
 *
 * Must sit *inside* RequireAuth (which has already validated the Cognito session
 * and populated the auth cache). It then uses useRole() — which reads from the
 * warm localStorage cache with no additional network call — to check whether the
 * current user's role is in the allowed list.
 *
 * Rendering behaviour:
 *   - isLoaded === false  → renders null (avoids a redirect flash before role resolves)
 *   - role in roles       → renders <Outlet /> (allows the child routes through)
 *   - role not in roles   → redirects to a role-appropriate landing page:
 *                           /platform/kyc for PlatformAdmin, /appointments for
 *                           Receptionists, /patients for Admins/Doctors
 *
 * Usage in a router config:
 *   <Route element={<RequireRoles roles={["Admin", "PlatformAdmin"]} />}>
 *     <Route path="/reports" element={<Reports />} />
 *   </Route>
 */

function fallbackRoute(role: UserRole): string {
  if (role === "") return "/login";  // cache miss + getMe() failed — re-auth
  // PlatformAdmin has no clinic pages (incl. /patients); send them to KYC review.
  // Must not fall through to /patients or a patients-gate would redirect-loop.
  if (role === "PlatformAdmin") return "/platform/kyc";
  if (role === "Receptionist") return "/appointments";
  return "/patients";
}

const RequireRoles: React.FC<RequireRolesProps> = ({ roles }) => {
  const { role, isLoaded } = useRole();

  // Still resolving — show nothing rather than flashing a redirect.
  if (!isLoaded) return null;

  if (roles.includes(role)) return <Outlet />;

  return <Navigate to={fallbackRoute(role)} replace />;
};

export default RequireRoles;
